import { useEffect, useMemo, useState } from "react";
import { LargeContentBox } from "../../components/common";
import { PageHeader } from "@/components/common/PageHeader";
import { CommonDialog } from "@/components/common/CommonDialog";
import {
  MedicalServices,
  Medication,
  Assignment,
  NoteAddOutlined,
  ShowChart,
  LocalHospital,
} from "@mui/icons-material";
import {
  Stack,
  Grid,
  Card,
  CardContent,
  Typography,
  DialogTitle,
  DialogContent,
  useTheme,
  Button,
  DialogActions,
} from "@mui/material";
import InsightsSubsectionPanel from "src-next/shared/ui/sections/InsightsSubsectionPanel";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useConditionByOrganization } from "@/services/fhir/condition/hooks/useConditionByOrganization";
import { useMedicationRequestByOrganization } from "@/services/fhir/medicationrequest/hooks/useMedicationRequestByOrganization";
import { useAppointmentStats } from "@/services/fhir/statistics/hooks/useAppointmentStats";
import { useLocationContext } from "@/contexts/LocationContext";
import { useServiceRequestByOrganization } from "@/services/fhir/servicerequest/hooks/useServiceRequestByOrganization";
import { DiagnosticAnalysis } from "./components/DiagnosticAnalysis";
import { PrescriptionAnalysis } from "./components/PrescriptionAnalysis";
import { ServiceRequestAnalysis } from "./components/ServiceRequestAnalysis";
import { ProtocolAnalysis } from "./components/ProtocolAnalysis";
import { AppointmentAnalysis } from "./components/AppointmentAnalysis";
import { AllergiesAnalysis } from "./components/AllergiesAnalysis";
import { SlotAnalysis } from "./components/SlotAnalysis";
import { ConductAnalysis } from "./components/ConductAnalysis";
import { ProblemListAnalysis } from "./components/ProblemListAnalysis";
import { TreatmentPlansAnalysis } from "./components/TreatmentPlansAnalysis";

// Interface para os tipos de análise
type AnalysisType =
  | "diagnosticos"
  | "tratamentos"
  | "protocolos"
  | "consultas"
  | "service"
  | "alergias"
  | "slots"
  | "condutas"
  | "problemas"
  | "planos"
  | null;

// Interface para os cards de análise
interface AnalysisCard {
  active: boolean;
  id: NonNullable<AnalysisType>;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  total: number | undefined;
  component: React.ReactElement;
}

// Interface para as seções de análise
interface AnalysisSection {
  title: string;
  subtitle?: string;
  icon: React.ElementType;
  panelId: string;
  cards: AnalysisCard[];
}

export function ClinicalAnalysis() {
  const theme = useTheme();

  const { organizationId } = useOrganizationContext();
  const { location } = useLocationContext();
  // Estado para controlar qual dialog está aberto
  const [openDialog, setOpenDialog] = useState<AnalysisType>(null);

  const actors = useMemo(() => {
    return (
      location?.map((loc) => `Location/${loc.resource?.id}`).filter(Boolean) ||
      []
    );
  }, [location]);

  const {
    data: conditionData,
    loading: conditionLoading,
    fetchConditions,
  } = useConditionByOrganization({
    organizationId,
    // startDate: dateRange?.startDate,
    // endDate: dateRange?.endDate,

    summary: true,
    includeEncounter: false,
  });

  const {
    data: medData,
    loading: medLoading,
    fetchMedicationRequests,
  } = useMedicationRequestByOrganization({
    organizationId,
    // startDate: dateRange?.startDate,
    // endDate: dateRange?.endDate,
    summary: true,
    includeEncounter: false,
  });

  const {
    data: serviceData,
    loading: serviceLoading,
    fetchServiceRequests,
  } = useServiceRequestByOrganization({
    organizationId,
    // startDate: dateRange?.startDate,
    // endDate: dateRange?.endDate,
    summary: true,
    includeEncounter: false,
  });

  const {
    data: appointmentData,
    loading: loadingAppointment,
    fetchStats: fetchAppointment,
  } = useAppointmentStats({
    actors,
    // ...filters,
    // startDate: dateRange?.startDate,
    // endDate: dateRange?.endDate,
    // status: ["fulfilled", "booked", "pending",'cancelled'],
    summary: true,
  });

  // Função para fechar o dialog
  const handleCloseDialog = () => {
    setOpenDialog(null);
  };

  // Configuração dos cards de análise organizados por seções
  const analysisSections: AnalysisSection[] = [
    {
      title: "Tratamentos, Protocolos e Documentação Clínica",
      subtitle:
        "Prescrições, protocolos, condutas e planos terapêuticos da equipe",
      icon: Medication,
      panelId: "clinical-treatments",
      cards: [
        {
          active: true,
          id: "tratamentos" as const,
          title: "Tratamentos Prescritos",
          description:
            "Identifique os medicamentos mais prescritos e padrões de tratamento para otimizar o estoque e padronizar condutas",
          icon: Medication,
          color: theme.palette.success.main,
          total: undefined,
          component: <PrescriptionAnalysis count={medData?.total} />,
        },
        {
          active: true,
          id: "protocolos" as const,
          title: "Protocolos Aplicados",
          description:
            "Descubra quais protocolos clínicos são mais utilizados e padronize as condutas da equipe",
          icon: Assignment,
          color: theme.palette.warning.main,
          total: undefined,
          component: <ProtocolAnalysis />,
        },
        {
          active: true,
          id: "condutas" as const,
          title: "Condutas",
          description:
            "Monitore o andamento das condutas médicas e identifique gargalos no fluxo de atendimento",
          icon: LocalHospital,
          color: theme.palette.secondary.main,
          total: undefined,
          component: <ConductAnalysis />,
        },
        {
          active: true,
          id: "problemas" as const,
          title: "Lista de Problemas",
          description:
            "Visualize a evolução dos problemas de saúde e priorize ações para os casos mais críticos",
          icon: ShowChart,
          color: theme.palette.warning.main,
          total: undefined,
          component: <ProblemListAnalysis />,
        },
        {
          active: true,
          id: "planos" as const,
          title: "Planos de Tratamento",
          description:
            "Acompanhe a efetividade dos planos terapêuticos e o progresso dos pacientes em tratamento",
          icon: ShowChart,
          color: theme.palette.primary.main,
          total: undefined,
          component: <TreatmentPlansAnalysis />,
        },
      ],
    },
    {
      title: "Diagnósticos e Exames",
      subtitle: "Condições de saúde, solicitações de exames e alergias",
      icon: MedicalServices,
      panelId: "clinical-diagnostics",
      cards: [
        {
          active: true,
          id: "diagnosticos" as const,
          title: "Diagnósticos Efetuados",
          description:
            "Descubra as condições de saúde mais prevalentes na sua população e direcione ações preventivas",
          icon: MedicalServices,
          color: theme.palette.primary.main,
          total: undefined,
          component: <DiagnosticAnalysis count={conditionData?.total} />,
        },
        {
          active: true,
          id: "service" as const,
          title: "Solicitações de Exames",
          description:
            "Analise quais exames são mais solicitados e otimize a gestão de recursos diagnósticos",
          icon: Medication,
          color: theme.palette.success.dark,
          total: undefined,
          component: <ServiceRequestAnalysis count={serviceData?.total} />,
        },
        {
          active: true,
          id: "alergias" as const,
          title: "Alergias",
          description:
            "Mapeie as alergias mais comuns e reduza riscos de reações adversas nos atendimentos",
          icon: NoteAddOutlined,
          color: theme.palette.error.main,
          total: undefined,
          component: <AllergiesAnalysis />,
        },
      ],
    },
  ];

  // Flatten todos os cards para busca no dialog
  const allCards: AnalysisCard[] = analysisSections.flatMap(
    (section) => section.cards
  ) as AnalysisCard[];

  // Função para renderizar o conteúdo do dialog baseado no tipo
  const renderDialogContent = () => {
    const dialogTitles: Record<NonNullable<AnalysisType>, string> = {
      diagnosticos: "Diagnósticos Efetuados",
      tratamentos: "Tratamentos Prescritos",
      protocolos: "Protocolos Aplicados",
      consultas: "Consultas",
      service: "Exames Prescritos",
      alergias: "Alergias",
      slots: "Horários",
      condutas: "Condutas",
      problemas: "Lista de Problemas",
      planos: "Planos de Tratamento",
    };

    if (!openDialog) return null;

    return (
      <>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          {dialogTitles[openDialog]}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ py: 2 }}>
            {openDialog &&
              allCards.find((card) => card.id === openDialog)?.component}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fechar</Button>
        </DialogActions>
      </>
    );
  };

  useEffect(() => {
    if (organizationId) {
      Promise.all([
        fetchConditions(),
        fetchMedicationRequests(),
        fetchAppointment(),
        fetchServiceRequests(),
      ]);
    }
  }, [
    organizationId,
    fetchConditions,
    fetchMedicationRequests,
    fetchAppointment,
    fetchServiceRequests,
  ]);

  return (
    <LargeContentBox>
      <Stack width="100%" spacing={3}>
        {/* Cabeçalho da página */}
        <PageHeader
          title="Insights clínicos"
          icon={ShowChart}
          description="Visualize indicadores clínicos importantes para análise da saúde populacional e de seus pacientes"
        />

        {/* Seções de cards de análise */}
        <Stack spacing={2} width="100%">
          {analysisSections.map((section) => {
            const SectionIcon = section.icon;

            return (
              <InsightsSubsectionPanel
                key={section.panelId}
                panelId={section.panelId}
                icon={<SectionIcon sx={{ fontSize: 18 }} />}
                title={section.title}
                subtitle={section.subtitle}
              >
                <Grid container spacing={2}>
                  {section.cards
                    ?.sort((a, b) =>
                      a.active === b.active ? 0 : a.active ? -1 : 1
                    )
                    ?.map((card) => (
                      <Grid item xs={12} sm={6} key={card.id}>
                        <Card
                          sx={{
                            cursor: card.active ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "6px",
                            boxShadow: "none",
                            opacity: card.active ? 1 : 0.7,
                            position: "relative",
                            overflow: "hidden",
                            "&:hover": {
                              borderColor: card.color,
                              bgcolor: `${card.color}08`,
                            },
                          }}
                          onClick={() => card.active && setOpenDialog(card.id)}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <card.icon
                                  sx={{
                                    fontSize: 28,
                                    color: card.active
                                      ? card.color
                                      : theme.palette.text.disabled,
                                  }}
                                />

                                <Typography
                                  variant="subtitle1"
                                  fontWeight={500}
                                  flex={1}
                                  color={
                                    card.active
                                      ? "text.primary"
                                      : "text.disabled"
                                  }
                                >
                                  {card.title}
                                </Typography>

                                {card.total !== undefined && (
                                  <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    color={
                                      card.active
                                        ? card.color
                                        : theme.palette.text.disabled
                                    }
                                  >
                                    {card.total}
                                  </Typography>
                                )}
                              </Stack>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ pl: "44px" }}
                              >
                                {card.description}
                                {!card.active && (
                                  <Typography
                                    component="span"
                                    color="text.disabled"
                                    sx={{
                                      display: "block",
                                      fontStyle: "italic",
                                      mt: 0.5,
                                    }}
                                  >
                                    Em desenvolvimento
                                  </Typography>
                                )}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </InsightsSubsectionPanel>
            );
          })}
        </Stack>

        {/* Dialog para exibir as análises */}

        {openDialog && (
          <CommonDialog
            isOpen={openDialog !== null}
            handleClose={handleCloseDialog}
            maxWidth="lg"
          >
            {renderDialogContent()}
          </CommonDialog>
        )}
      </Stack>
    </LargeContentBox>
  );
}
